import { Controller } from "@hotwired/stimulus"
import { FreehandPen, closesTheGap } from "../lib/area_drawing"
import { track } from "../lib/mixpanel"

// Loads the Google Maps JS API (maps + places) once, shared across instances.
let googleMapsPromise = null
function loadGoogleMaps() {
  if (window.google?.maps?.places) return Promise.resolve()
  if (googleMapsPromise) return googleMapsPromise

  const apiKey = document.querySelector('meta[name="google-maps-api-key"]')?.content
  if (!apiKey) return Promise.reject("No Google Maps API key")

  googleMapsPromise = new Promise((resolve, reject) => {
    window._initGcMaps = () => { delete window._initGcMaps; resolve() }
    const script = document.createElement("script")
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=_initGcMaps`
    script.async = true
    script.onerror = () => { googleMapsPromise = null; reject("Failed to load Google Maps") }
    document.head.appendChild(script)
  })
  return googleMapsPromise
}

// Minimal IANA → Rails (ActiveSupport) zone-name map for sensible defaults.
// Anything not listed falls back to the server default.
const TZ_MAP = {
  "America/New_York": "Eastern Time (US & Canada)",
  "America/Detroit": "Eastern Time (US & Canada)",
  "America/Chicago": "Central Time (US & Canada)",
  "America/Denver": "Mountain Time (US & Canada)",
  "America/Boise": "Mountain Time (US & Canada)",
  "America/Phoenix": "Arizona",
  "America/Los_Angeles": "Pacific Time (US & Canada)",
  "America/Anchorage": "Alaska",
  "Pacific/Honolulu": "Hawaii"
}

/**
 * Simplified location picker for ConsLog Builder (GC) — lives inside the
 * "Name & Location" step of the project wizard.
 *
 * Three modes (toggle): search address, drop a pin, lat/long. A satellite
 * map with a draggable pin shows in every mode. Picking a place / dragging
 * the pin / typing coordinates keep the hidden project[...] fields in sync
 * (address, city, state, zipcode, latitude, longitude, time_zone). The
 * hidden latitude field carries data-required so the wizard's "Next"
 * validation blocks until a location is set.
 */
export default class extends Controller {
  static targets = [
    "search", "map", "mapWrap", "mapNote", "coordChip",
    "latField", "lngField", "latInput", "lngInput",
    "address", "city", "state", "zip", "tz",
    "seg", "panel", "satBtn", "mapBtn",
    "pickModal", "pickMap", "pickSearch", "pickSpot", "pickCoords", "pickUseBtn", "pickedLabel",
    "areaSection", "areaMode", "areaDrawTools", "areaShapeField",
    "areaRadius", "areaRadiusField", "areaCustom", "areaCustomUnit", "drawTools", "drawToggle", "drawHint", "undoBtn", "eraseBtn"
  ]
  static values = { lat: Number, lng: Number }

  connect() {
    this._syncMode()
    this._presetTimezone()
    loadGoogleMaps()
      .then(() => this._initMaps())
      .catch((e) => {
        console.warn("GC location — maps unavailable:", e)
        if (this.hasMapWrapTarget) this.mapWrapTarget.classList.add("hidden")
        if (this.hasMapNoteTarget) this.mapNoteTarget.classList.remove("hidden")
      })
  }

  // A draft brought back (Phelipe 2026-10-09: "I don't see the map button"):
  // the draft controller wraps the form and restores the ticked choice before
  // this connects, so follow the ticked radio here, without opening the pop-up,
  // and say what spot was picked.
  _syncMode() {
    this.mode = this.segTargets.find((r) => r.checked)?.dataset.mode || "search"
    this.panelTargets.forEach((p) => p.classList.toggle("hidden", p.dataset.mode !== this.mode))
    if (this.mode === "pickmap") this._labelPickedSpot()
  }

  _labelPickedSpot() {
    if (!this.hasPickedLabelTarget || !this.hasLatFieldTarget) return
    const lat = parseFloat(this.latFieldTarget.value), lng = parseFloat(this.lngFieldTarget.value)
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return
    const where = [this.hasAddressTarget && this.addressTarget.value, this.hasCityTarget && this.cityTarget.value, this.hasStateTarget && this.stateTarget.value]
      .filter(Boolean).join(", ")
    this.pickedLabelTarget.textContent = [where, this._coordsText(lat, lng)].filter(Boolean).join(" · ")
  }

  disconnect() {
    if (this._autocomplete) google.maps.event.clearInstanceListeners(this._autocomplete)
    if (this._pickAutocomplete) google.maps.event.clearInstanceListeners(this._pickAutocomplete)
    document.removeEventListener("keydown", this._onPickKey)
  }

  // ── Mode toggle (radio: search / coords) ──────────────────────────
  switchMode(e) {
    if (!e.currentTarget.checked) return // a draft's restore fires change on every radio
    this.mode = e.currentTarget.dataset.mode
    this.panelTargets.forEach((p) => p.classList.toggle("hidden", p.dataset.mode !== this.mode))
    if (this._map) google.maps.event.trigger(this._map, "resize")
    if (this.mode === "pickmap") this.openPickMap()
  }

  // ── Pick on map (Phelipe 2026-10-08) ──────────────────────────────
  // A big map in a pop-up: search an address or a city, zoom in, click the
  // job site (drag the pin to adjust), then "Use this spot".
  openPickMap() {
    if (!this.hasPickModalTarget) return
    this.pickModalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
    this._onPickKey = (e) => { if (e.key === "Escape") this.closePickMap() }
    document.addEventListener("keydown", this._onPickKey)

    loadGoogleMaps().then(() => {
      if (!this._pickMap) this._initPickMap()
      if (!this._geocoder) this._geocoder = new google.maps.Geocoder()
      // Start from the spot already chosen, if any.
      const lat = parseFloat(this.latFieldTarget.value), lng = parseFloat(this.lngFieldTarget.value)
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        this._pickAt(lat, lng, { recenter: true, zoom: 18, geocode: false })
        this.pickSpotTarget.textContent = this.hasSearchTarget && this.searchTarget.value ? this.searchTarget.value : `${lat.toFixed(5)}, ${lng.toFixed(5)}`
      }
      google.maps.event.trigger(this._pickMap, "resize")
      if (this.hasPickSearchTarget) this.pickSearchTarget.focus()
    }).catch(() => {
      this.pickSpotTarget.textContent = "The map isn't available right now. You can still type the address."
    })
  }

  closePickMap() {
    if (!this.hasPickModalTarget) return
    this.pickModalTarget.classList.add("hidden")
    document.body.style.overflow = ""
    document.removeEventListener("keydown", this._onPickKey)
  }

  // Only a click on the dimmed backdrop itself closes it.
  closePickMapFromBackdrop(event) {
    if (event.target === event.currentTarget) this.closePickMap()
  }

  usePickedSpot() {
    // The time clock area first: a drawing needs 3 corners; with no pin yet,
    // the pin goes in the middle of the drawing.
    if (this._areaMode() === "drawn") {
      const corners = this._cornerList()
      if (corners.length < 3) {
        this.pickSpotTarget.textContent = "Click the map at least 3 times to draw the area."
        return
      }
      if (this.hasAreaShapeFieldTarget) this.areaShapeFieldTarget.value = JSON.stringify(corners)
      if (this.hasAreaRadiusFieldTarget) this.areaRadiusFieldTarget.value = ""
      if (!this._picked) {
        const lats = corners.map((c) => c[0]), lngs = corners.map((c) => c[1])
        this._pickAt((Math.min(...lats) + Math.max(...lats)) / 2, (Math.min(...lngs) + Math.max(...lngs)) / 2, { geocode: false })
      }
    } else {
      // A size of their own must be a real one (Phelipe 2026-10-09).
      if (this._customSizeOn()) {
        const problem = this._customSizeProblem()
        if (problem) { this.pickSpotTarget.textContent = problem; this.areaCustomTarget.focus(); return }
      }
      if (this.hasAreaShapeFieldTarget) this.areaShapeFieldTarget.value = ""
      // A size picked from the list or typed, in meters; empty keeps the company's job area size.
      if (this.hasAreaRadiusFieldTarget) this.areaRadiusFieldTarget.value = this._areaRadiusM() ?? ""
    }
    this._showAreaOnFormMap()
    if (!this._picked) return
    const { lat, lng } = this._picked
    this._setLocation(lat, lng, { recenter: true, zoom: 18 })
    if (this._pickedPlace) {
      this._fillAddressFromComponents(this._pickedPlace.components, this._pickedPlace.formatted)
    } else {
      this._reverseGeocode(lat, lng)
    }
    if (this.hasPickedLabelTarget) this.pickedLabelTarget.textContent = `${this.pickSpotTarget.textContent} · ${this._coordsText(lat, lng)}`
    track("Project Location Picked On Map", { project_phase: "construction" })
    this.closePickMap()
  }

  _initPickMap() {
    this._pickMap = new google.maps.Map(this.pickMapTarget, {
      center: { lat: 39.5, lng: -98.35 }, zoom: 4, mapTypeId: "hybrid", tilt: 0,
      streetViewControl: false, fullscreenControl: false, gestureHandling: "greedy",
      // Plain + / − buttons (Phelipe 2026-10-08: "let me zoom in more") instead
      // of Google's round camera control, and as close as the photos go.
      zoomControl: true, cameraControl: false, maxZoom: 21,
      zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_CENTER },
      mapTypeControl: true,
      mapTypeControlOptions: { mapTypeIds: ["hybrid", "roadmap"], position: google.maps.ControlPosition.TOP_RIGHT }
    })
    this._pickMarker = new google.maps.Marker({ map: this._pickMap, draggable: true, visible: false })
    this._pickMarker.addListener("dragend", () => {
      const p = this._pickMarker.getPosition()
      this._pickAt(p.lat(), p.lng())
    })
    this._pickMap.addListener("click", (ev) => {
      // Drawing the time clock area with the pencil on: a click is a corner, not the pin.
      if (this._areaMode() === "drawn" && this._drawing) return this._addCorner(ev.latLng)
      this._pickAt(ev.latLng.lat(), ev.latLng.lng())
    })
    this._initAreaDrawing()

    if (this.hasPickSearchTarget) {
      // Enter picks the suggestion; it must never submit the project form.
      this.pickSearchTarget.addEventListener("keydown", (e) => { if (e.key === "Enter") e.preventDefault() })
      this._pickAutocomplete = new google.maps.places.Autocomplete(this.pickSearchTarget, {
        fields: ["address_components", "formatted_address", "geometry", "name", "types"]
      })
      this._pickAutocomplete.addListener("place_changed", () => {
        const place = this._pickAutocomplete.getPlace()
        // Enter without picking a suggestion: look up what was typed
        // ("Idaho", "Burley ID") and go there.
        if (!place?.geometry) { this._goToTyped(place?.name || this.pickSearchTarget.value); return }
        const loc = place.geometry.location
        const exact = (place.types || []).some((t) => ["street_address", "premise", "subpremise", "establishment", "point_of_interest"].includes(t))
        if (exact) {
          // An address: drop the pin on it, ready to adjust.
          this._pickAt(loc.lat(), loc.lng(), { recenter: true, zoom: 19, geocode: false })
          this._pickedPlace = { components: place.address_components, formatted: place.formatted_address }
          this.pickSpotTarget.textContent = place.formatted_address || place.name || ""
        } else if (place.geometry.viewport) {
          // A city or an area: go there and let them zoom in and click.
          this._pickMap.fitBounds(place.geometry.viewport)
        } else {
          this._pickMap.setCenter(loc)
          this._pickMap.setZoom(14)
        }
      })
    }
  }

  // ── The job's time clock area (Phelipe 2026-10-09) ────────────────
  // "Draw around the project to create the geofence": an outline drawn
  // corner by corner on the big map, saved as the job's area on Create; the
  // phone's time clock shows the job there. Left alone, the company's job
  // area size around the pin. Same tools as the Location box (K3-25).
  _areaMode() {
    return this.areaModeTargets.find((r) => r.checked)?.value || "company"
  }

  _initAreaDrawing() {
    if (!this.hasAreaSectionTarget) return
    let saved = []
    try { saved = JSON.parse(this.hasAreaShapeFieldTarget ? this.areaShapeFieldTarget.value || "[]" : "[]") } catch { saved = [] }
    this._corners = new google.maps.MVCArray(saved.map(([lat, lng]) => new google.maps.LatLng(lat, lng)))
    // GOTCHA (seen 2026-10-09 on Maps 3.66): a Polygon given an EMPTY MVCArray as
    // `paths` takes it for a list of paths, so every corner pushed later counts as
    // its own path ("a.forEach is not a function") and nothing ever draws. Hand it
    // the list of paths explicitly, with our corners as the one path inside.
    this._polygon = new google.maps.Polygon({
      map: this._pickMap, paths: new google.maps.MVCArray([ this._corners ]), editable: true, visible: false,
      strokeColor: "#f97316", strokeWeight: 2, fillColor: "#f97316", fillOpacity: 0.18
    })
    // The size around the pin, as a circle, while the first choice is on.
    if (google.maps.Circle) this._circle = new google.maps.Circle({ map: this._pickMap, clickable: false, strokeWeight: 2, visible: false })
    // The pencil (Phelipe 2026-10-09) sits on the map's bottom-right corner and
    // turns drawing on and off while "Draw it on the map" is chosen.
    const slot = google.maps.ControlPosition?.RIGHT_BOTTOM
    const tools = this.hasDrawToolsTarget ? this.drawToolsTarget : (this.hasDrawToggleTarget ? this.drawToggleTarget : null)
    if (tools && slot !== undefined && this._pickMap.controls?.[slot]) this._pickMap.controls[slot].push(tools)
    // Pencil on: draw around the job with the mouse held down (or tap corners).
    this._pen = new FreehandPen({ container: this.pickMapTarget, map: this._pickMap,
      onTap: (latLng) => this._addCorner(latLng), onStroke: (points) => this._setCorners(points) })
    this._drawing = false
    // What Undo goes back to, and the shape's look: filled once it closes (3+ corners), like the phone.
    this._history = []
    ;["insert_at", "set_at", "remove_at"].forEach((ev) => this._corners.addListener(ev, () => this._paintShape()))
    this._paintShape()
    // A drawing kept in the form (a draft brought back): show it, drawn mode on.
    if (saved.length >= 3) {
      const drawn = this.areaModeTargets.find((r) => r.value === "drawn")
      if (drawn) { drawn.checked = true; this.areaModeChanged() }
    }
  }

  areaModeChanged() {
    const drawn = this._areaMode() === "drawn"
    if (this.hasAreaDrawToolsTarget) this.areaDrawToolsTarget.classList.toggle("hidden", !drawn)
    this._polygon?.setVisible(drawn)
    // Choosing Draw shows the pencil, Undo and Start over, pencil on; back to a size hides them.
    if (this.hasDrawToolsTarget) this.drawToolsTarget.classList.toggle("hidden", !drawn)
    else if (this.hasDrawToggleTarget) this.drawToggleTarget.classList.toggle("hidden", !drawn)
    // The pen comes on for a fresh drawing; an outline already there starts with it off.
    this._setDrawing(drawn && this._cornerList().length < 3)
    this._drawCircle()
    if (this.hasPickUseBtnTarget) this.pickUseBtnTarget.disabled = !this._picked && !(drawn && this._cornerList().length >= 3)
  }

  // The pencil: on, drawing with the mouse held down makes the outline and a
  // tap adds a corner; off, corners can be dragged, a click moves the pin and
  // the map pans freely while the drawing stays.
  toggleDrawing() {
    this._setDrawing(!this._drawing)
  }

  // A stroke replaces the outline (Undo brings the old one back).
  _setCorners(points, { remember = true } = {}) {
    if (!this._corners) return
    if (remember) this._remember()
    this._corners.clear()
    points.slice(0, 40).forEach((p) => this._corners.push(new google.maps.LatLng(p.lat, p.lng)))
    if (this.hasPickUseBtnTarget && this._corners.getLength() >= 3) this.pickUseBtnTarget.disabled = false
    // A released stroke is a finished drawing (an Undo only restores the corners).
    if (remember && this._corners.getLength() >= 3) this._finishDrawing()
  }

  _setDrawing(on) {
    this._drawing = !!on
    if (this.hasDrawToggleTarget) {
      const b = this.drawToggleTarget
      b.setAttribute("aria-pressed", String(this._drawing))
      b.classList.toggle("bg-brand", this._drawing)
      b.classList.toggle("text-white", this._drawing)
      b.classList.toggle("bg-white", !this._drawing)
      b.classList.toggle("text-gray-600", !this._drawing)
      b.title = (this._drawing ? b.dataset.titleOn : b.dataset.titleOff) || ""
    }
    if (this.hasDrawHintTarget) this.drawHintTarget.textContent = (this._drawing ? this.drawHintTarget.dataset.on : this.drawHintTarget.dataset.off) || ""
    this._pen?.enable(this._drawing)
    this._polygon?.setOptions?.({ clickable: !this._drawing })
    this._pickMap?.setOptions?.({ draggableCursor: this._drawing ? "crosshair" : null })
    this._paintShape()
  }

  // The first choice's size in meters; null = the company's job area size.
  // "Custom…" takes the number typed in the company's feet or meters.
  _areaRadiusM() {
    if (!this.hasAreaRadiusTarget) return null
    if (this._customSizeOn()) {
      const typed = Number(this.areaCustomTarget.value)
      if (!Number.isFinite(typed) || typed <= 0) return null
      const meters = this.areaCustomTarget.dataset.unit === "m" ? typed : typed * 0.3048
      return Math.round(meters * 100) / 100
    }
    const n = Number(this.areaRadiusTarget.value)
    return Number.isFinite(n) && n > 0 ? n : null
  }

  _customSizeOn() {
    return this.hasAreaCustomTarget && this.hasAreaRadiusTarget && this.areaRadiusTarget.value === "custom"
  }

  // Why a typed size can't be used yet, in the person's words; null when it's fine.
  _customSizeProblem() {
    const box = this.areaCustomTarget
    const typed = Number(box.value)
    if (box.value.trim() === "" || !Number.isFinite(typed) || typed <= 0) return box.dataset.needed || "Type the size around the pin."
    const min = Number(box.min), max = Number(box.max)
    if ((Number.isFinite(min) && typed < min) || (Number.isFinite(max) && typed > max)) return box.dataset.range || ""
    return null
  }

  // Picking a size is picking the circle: the first choice goes on if it wasn't.
  // Custom… opens the number box; any other choice puts it away.
  areaRadiusChanged(event) {
    const custom = this._customSizeOn()
    if (this.hasAreaCustomTarget) {
      const wasHidden = this.areaCustomTarget.hidden
      this.areaCustomTarget.hidden = !custom
      if (this.hasAreaCustomUnitTarget) this.areaCustomUnitTarget.hidden = !custom
      if (custom && wasHidden && event?.target === this.areaRadiusTarget) this.areaCustomTarget.focus()
    }
    const company = this.areaModeTargets.find((r) => r.value === "company")
    if (company && !company.checked) { company.checked = true; return this.areaModeChanged() }
    this._drawCircle()
  }

  // The circle around the pin for the first choice: gray for the company's
  // size, orange for one picked from the list. Nothing without a pin.
  _drawCircle() {
    if (!this._circle) return
    const own = this._areaRadiusM()
    const radius = own ?? Number(this.hasAreaRadiusTarget ? this.areaRadiusTarget.dataset.companyM : NaN)
    const show = this._areaMode() !== "drawn" && !!this._picked && Number.isFinite(radius) && radius > 0
    this._circle.setVisible(show)
    if (!show) return
    const color = own ? "#f97316" : "#6b7280"
    this._circle.setOptions({ strokeColor: color, fillColor: color, fillOpacity: own ? 0.15 : 0.08 })
    this._circle.setCenter(this._picked)
    this._circle.setRadius(radius)
  }

  _addCorner(latLng) {
    if (!this._corners || this._corners.getLength() >= 40) return
    // Back on the first corner: the loop is closed, the drawing is done.
    if (closesTheGap(this._corners.getArray(), latLng, this._pickMap?.getZoom?.())) return this._finishDrawing()
    this._remember()
    this._corners.push(latLng)
    if (this.hasPickUseBtnTarget && this._corners.getLength() >= 3) this.pickUseBtnTarget.disabled = false
  }

  // Done drawing (the loop closed, or a stroke released): the pencil goes off,
  // the area lights up, and the corners can be dragged.
  _finishDrawing() {
    this._setDrawing(false)
    this._paintShape()
    this._afterChange()
  }

  // Undo goes back one step: the last stroke, or the last corner.
  undoCorner() {
    if (!this._corners) return
    const before = this._history?.pop()
    if (before) this._setCorners(before, { remember: false })
    else if (this._corners.getLength()) this._corners.pop()
    this._afterChange()
  }

  clearCorners() {
    if (!this._corners?.getLength()) return
    this._remember()
    this._corners.clear()
    this._afterChange()
  }

  _remember() {
    if (!this._history) this._history = []
    this._history.push(this._corners.getArray().map((p) => ({ lat: p.lat(), lng: p.lng() })))
    if (this._history.length > 50) this._history.shift()
  }

  _afterChange() {
    if (this.hasPickUseBtnTarget) this.pickUseBtnTarget.disabled = !this._picked && this._cornerList().length < 3
  }

  // Filled once the outline closes (3+ corners), stronger once the drawing is
  // done (pencil off), stroke only before; Undo and Start over light up when
  // there's something to take back.
  _paintShape() {
    const n = this._corners ? this._corners.getLength() : 0
    const fill = n < 3 ? 0 : (this._drawing ? 0.2 : 0.35)
    this._polygon?.setOptions?.({ fillOpacity: fill, strokeWeight: n >= 3 ? 3 : 2 })
    if (this.hasUndoBtnTarget) this.undoBtnTarget.disabled = n === 0 && !(this._history?.length)
    if (this.hasEraseBtnTarget) this.eraseBtnTarget.disabled = n === 0
  }

  _cornerList() {
    return this._corners ? this._corners.getArray().map((p) => [Number(p.lat().toFixed(7)), Number(p.lng().toFixed(7))]) : []
  }

  // The drawing on the form's own map too, once it's used (not editable there).
  _showAreaOnFormMap() {
    if (!this._map || !window.google?.maps) return
    let corners = []
    try { corners = JSON.parse(this.hasAreaShapeFieldTarget ? this.areaShapeFieldTarget.value || "[]" : "[]") } catch { corners = [] }
    if (!this._formPolygon) {
      this._formPolygon = new google.maps.Polygon({ map: this._map, clickable: false,
        strokeColor: "#f97316", strokeWeight: 2, fillColor: "#f97316", fillOpacity: 0.18 })
    }
    this._formPolygon.setPaths(corners.map(([lat, lng]) => ({ lat, lng })))
    this._formPolygon.setVisible(corners.length >= 3)
    // A size picked from the list: its circle around the pin on the form's map too.
    const radius = Number(this.hasAreaRadiusFieldTarget ? this.areaRadiusFieldTarget.value : NaN)
    if (google.maps.Circle && !this._formCircle) {
      this._formCircle = new google.maps.Circle({ map: this._map, clickable: false, strokeWeight: 2,
        strokeColor: "#f97316", fillColor: "#f97316", fillOpacity: 0.15, visible: false })
    }
    if (this._formCircle) {
      const show = Number.isFinite(radius) && radius > 0 && corners.length < 3 && !!this._picked
      this._formCircle.setVisible(show)
      if (show) { this._formCircle.setCenter(this._picked); this._formCircle.setRadius(radius) }
    }
  }

  _coordsText(lat, lng) {
    return `Latitude ${lat.toFixed(6)} · Longitude ${lng.toFixed(6)}`
  }

  // A city, state or area typed in the search: fit the map to it; a plain
  // point (no area) gets a close zoom. Never drops the pin — they click it.
  _goToTyped(text) {
    const query = String(text || "").trim()
    if (!query || !this._geocoder) return
    this._geocoder.geocode({ address: query }, (results, status) => {
      if (status !== "OK" || !results[0]) {
        this.pickSpotTarget.textContent = `Couldn't find "${query}". Try a city, a state or an address.`
        return
      }
      const r = results[0]
      if (r.geometry.viewport) this._pickMap.fitBounds(r.geometry.viewport)
      else { this._pickMap.setCenter(r.geometry.location); this._pickMap.setZoom(15) }
    })
  }

  _pickAt(lat, lng, { recenter = false, zoom = null, geocode = true } = {}) {
    this._picked = { lat, lng }
    this._pickedPlace = null
    this._pickMarker.setPosition({ lat, lng })
    this._pickMarker.setVisible(true)
    this._drawCircle()
    if (recenter) { this._pickMap.setCenter({ lat, lng }); if (zoom) this._pickMap.setZoom(zoom) }
    if (this.hasPickUseBtnTarget) this.pickUseBtnTarget.disabled = false
    this.pickSpotTarget.textContent = `${lat.toFixed(5)}, ${lng.toFixed(5)}`
    if (this.hasPickCoordsTarget) {
      this.pickCoordsTarget.textContent = this._coordsText(lat, lng)
      this.pickCoordsTarget.classList.remove("hidden")
    }
    if (geocode && this._geocoder) {
      const asked = this._picked
      this._geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status !== "OK" || !results[0] || this._picked !== asked) return
        this._pickedPlace = { components: results[0].address_components, formatted: results[0].formatted_address }
        this.pickSpotTarget.textContent = results[0].formatted_address
      })
    }
  }

  // Satellite / Map base-layer toggle
  showSatellite() { this._setMapType("hybrid") }
  showRoadmap() { this._setMapType("roadmap") }
  _setMapType(t) {
    if (this._map) this._map.setMapTypeId(t)
    this.satBtnTarget?.classList.toggle("bg-brand", t === "hybrid")
    this.satBtnTarget?.classList.toggle("text-white", t === "hybrid")
    this.mapBtnTarget?.classList.toggle("bg-brand", t === "roadmap")
    this.mapBtnTarget?.classList.toggle("text-white", t === "roadmap")
  }

  // Manual lat/long inputs → move the pin
  coordsEntered() {
    const lat = parseFloat(this.latInputTarget.value)
    const lng = parseFloat(this.lngInputTarget.value)
    if (Number.isFinite(lat) && Number.isFinite(lng)) this._setLocation(lat, lng, { recenter: true })
  }

  // ── Maps init ─────────────────────────────────────────────────────
  _initMaps() {
    // The pin to start from: the project's, or the one a draft brought back.
    const saved = { lat: parseFloat(this.latFieldTarget.value), lng: parseFloat(this.lngFieldTarget.value) }
    const fromValues = Number.isFinite(this.latValue) && this.latValue !== 0
    const fromDraft = !fromValues && Number.isFinite(saved.lat) && Number.isFinite(saved.lng) && saved.lat !== 0
    const hasStart = fromValues || fromDraft
    const start = fromValues ? { lat: this.latValue, lng: this.lngValue } : (fromDraft ? saved : { lat: 39.5, lng: -98.35 })
    if (fromDraft && this.hasCoordChipTarget) {
      this.coordChipTarget.textContent = `${saved.lat.toFixed(5)}, ${saved.lng.toFixed(5)}`
      this.coordChipTarget.classList.remove("hidden")
    }

    this._map = new google.maps.Map(this.mapTarget, {
      center: start,
      zoom: hasStart ? 18 : 4,
      mapTypeId: "hybrid",
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
      gestureHandling: "greedy"
    })
    this._setMapType("hybrid")
    this._showAreaOnFormMap() // a drawn time clock area kept in a draft

    this._marker = new google.maps.Marker({ map: this._map, draggable: true, visible: hasStart })
    if (hasStart) this._marker.setPosition(start)
    this._geocoder = new google.maps.Geocoder()

    this._marker.addListener("dragend", () => {
      const p = this._marker.getPosition()
      this._setLocation(p.lat(), p.lng(), { recenter: false })
      this._reverseGeocode(p.lat(), p.lng())
    })
    // Clicking the map places the pin (and dragging moves it) in any mode.
    this._map.addListener("click", (ev) => {
      this._setLocation(ev.latLng.lat(), ev.latLng.lng(), { recenter: false })
      this._reverseGeocode(ev.latLng.lat(), ev.latLng.lng())
    })

    if (this.hasSearchTarget) {
      this._autocomplete = new google.maps.places.Autocomplete(this.searchTarget, {
        fields: ["address_components", "formatted_address", "geometry", "name"]
      })
      this._autocomplete.addListener("place_changed", () => this._onPlace())
    }
  }

  _onPlace() {
    const place = this._autocomplete.getPlace()
    if (!place?.geometry) return
    const loc = place.geometry.location
    this._fillAddressFromComponents(place.address_components, place.formatted_address)
    this._setLocation(loc.lat(), loc.lng(), { recenter: true, zoom: 18 })
  }

  // ── Shared helpers ────────────────────────────────────────────────
  _setLocation(lat, lng, { recenter = false, zoom = null } = {}) {
    const latStr = lat.toFixed(6)
    const lngStr = lng.toFixed(6)
    this.latFieldTarget.value = latStr
    this.lngFieldTarget.value = lngStr
    if (this.hasLatInputTarget) this.latInputTarget.value = latStr
    if (this.hasLngInputTarget) this.lngInputTarget.value = lngStr
    // Let the wizard clear any "location required" error on this field.
    this.latFieldTarget.dispatchEvent(new Event("change", { bubbles: true }))

    if (this._marker) { this._marker.setPosition({ lat, lng }); this._marker.setVisible(true) }
    if (this._map && recenter) { this._map.setCenter({ lat, lng }); this._map.setZoom(zoom || 18) }
    if (this.hasCoordChipTarget) {
      this.coordChipTarget.textContent = `${lat.toFixed(5)}, ${lng.toFixed(5)}`
      this.coordChipTarget.classList.remove("hidden")
    }
  }

  _reverseGeocode(lat, lng) {
    if (!this._geocoder) return
    this._geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        this._fillAddressFromComponents(results[0].address_components, results[0].formatted_address)
      }
    })
  }

  _fillAddressFromComponents(components, formatted) {
    if (!components) return
    const get = (type) => components.find((c) => c.types.includes(type))
    const streetNum = get("street_number")?.long_name || ""
    const route = get("route")?.long_name || ""
    const city = get("locality")?.long_name || get("postal_town")?.long_name ||
      get("sublocality")?.long_name || get("administrative_area_level_2")?.long_name || ""
    const state = get("administrative_area_level_1")?.short_name || ""
    const zip = get("postal_code")?.long_name || ""

    const street = [streetNum, route].filter(Boolean).join(" ")
    if (this.hasAddressTarget) this.addressTarget.value = street || formatted || ""
    if (this.hasCityTarget) this.cityTarget.value = city
    if (this.hasStateTarget) this.stateTarget.value = state
    if (this.hasZipTarget) this.zipTarget.value = zip
    if (this.hasSearchTarget && (street || formatted)) this.searchTarget.value = formatted || street
  }

  _presetTimezone() {
    if (!this.hasTzTarget || this.tzTarget.value) return
    try {
      const iana = Intl.DateTimeFormat().resolvedOptions().timeZone
      if (TZ_MAP[iana]) this.tzTarget.value = TZ_MAP[iana]
    } catch (_) { /* leave server default */ }
  }
}
